import { io, type Socket } from 'socket.io-client'
import { request } from './client'

/**
 * The teacher↔parent conversation, from both ends, plus its socket.
 *
 * 🔴 IT TURNS ON A BUTTON THAT HAS BEEN DISABLED SINCE THE PARENT TIER SHIPPED.
 * C2 carries *"Message the teacher"* with the tooltip *"Available once parent
 * messaging is switched on"*; this is that switch. It is the one thing a school
 * does daily that the product could only do through WhatsApp templates, which
 * are one-way, pre-approved, and cannot carry *"could you send a spare set of
 * clothes tomorrow"*.
 *
 * 🔴 SENDING IS AN HTTP WRITE, NEVER A SOCKET EMIT, AND THE SOCKET IS
 * RECEIVE-ONLY. The server mounts no inbound handlers at all. That keeps one
 * authorisation path for "may this person say this to that family" — the same
 * scoped, audited request every other write in this product makes — so the
 * durable row and the live frame can never disagree about who was allowed to
 * speak.
 *
 * ⚠️ AND THE FRAME IS THE MESSAGE ITSELF, WHICH THE STAFF CHANNEL DELIBERATELY
 * REFUSES TO DO. `staffEvents.ts` carries *"a pointer, not a payload"* because
 * it broadcasts to a whole school's room. This one's room is a single person —
 * `user:<id>` — so the payload is addressed to exactly the reader it is about.
 */

export type MessageSide = 'TEACHER' | 'PARENT'

export interface MessageRow {
  id: string
  senderRole: MessageSide
  senderLabel: string
  body: string
  createdAt: string
  /**
   * When the other side opened the thread.
   *
   * ⚠️ "SEEN", NOT "READ", AND THE UI MUST NOT PROMISE MORE. A server can know
   * the thread was fetched by the person it was addressed to and nothing else —
   * there are no ticks here, unlike the WhatsApp outbox, which has real receipts.
   */
  readAt: string | null
  /** True when the caller wrote it, so a client need not compare ids. */
  mine: boolean
  /** T8 — the template a staff message started from (a key, never text). */
  templateKey?: string | null
}

export interface MessageThread {
  playerId: string
  childName: string
  /** Oldest first: a conversation is read downwards. */
  messages: MessageRow[]
  /** 🔴 A name, never a phone number — `privacy-compliance.md` §5. */
  otherSide: string | null
  /**
   * P1-63 / C8 — the cursor for the page of OLDER messages ("Load earlier"),
   * null at the start of the conversation. Optional: an older API sent the
   * whole capped thread and no cursor.
   */
  nextBefore?: string | null
}

/** C8 — staff unread counts (teacher nav badge, class list). */
export interface StaffUnread {
  count: number
  byClass: { classId: string; count: number }[]
}

/** One row of a teacher's inbox: a child, and whether they are waiting. */
export interface ThreadSummary {
  playerId: string
  childName: string
  rollNumber: string | null
  classId: string | null
  lastAt: string | null
  lastBody: string | null
  lastFrom: MessageSide | null
  /** Messages from the other side this caller has not opened. */
  unread: number
}

interface Auth {
  token?: string
  signal?: AbortSignal
}

/**
 * The newest page of a thread, or — with `before` — the page older than that
 * message (P1-63, contract C8). Pages default to 50 on the server.
 */
export const getThread = (
  playerId: string,
  auth: Auth = {},
  page: { before?: string; limit?: number } = {},
): Promise<MessageThread> => {
  const q = new URLSearchParams()
  if (page.before) q.set('before', page.before)
  if (page.limit !== undefined) q.set('limit', String(page.limit))
  const qs = q.toString()
  return request(`/players/${encodeURIComponent(playerId)}/messages${qs ? `?${qs}` : ''}`, {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })
}

/**
 * ⚠️ THE BODY CARRIES THE TEXT AND NOTHING ELSE. There is deliberately no
 * `senderRole` field to set: the side comes from the token, or a client could
 * post a message that renders as the teacher's inside their own child's thread.
 */
export const sendMessage = (
  playerId: string,
  body: string,
  auth: Auth = {},
  /** T8 — staff only; the server drops one sent by a parent. A key, never text. */
  templateKey?: string,
): Promise<MessageRow> =>
  request(`/players/${encodeURIComponent(playerId)}/messages`, {
    method: 'POST',
    body: templateKey ? { body, templateKey } : { body },
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/** The teacher's inbox for one class — the whole roster, not only the threads. */
export const getInbox = (classId: string, auth: Auth = {}): Promise<ThreadSummary[]> =>
  request(`/classes/${encodeURIComponent(classId)}/messages`, {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/** C8 — parents' messages not yet opened, in the classes this staff member can see. */
export const getStaffUnread = (auth: Auth = {}): Promise<StaffUnread> =>
  request('/teacher/messages/unread', {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/** How many messages from school are waiting for this parent. */
export const getUnreadCount = (auth: Auth = {}): Promise<{ unread: number }> =>
  request('/parent/messages/unread', {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/* ──────────────────────────────────────────────────────────── the socket ── */

/** What arrives when the other side writes. Mirrors `MessagePush` on the server. */
export interface MessagePush {
  type: string
  id: string
  playerId: string
  /** So a teacher's notification names the child without opening the thread. */
  childName: string
  senderRole: MessageSide
  senderLabel: string
  body: string
  at: string
}

/**
 * ⚠️ THE SAME BASE THE REST CLIENT USES, AND READ THE SAME WAY. Blank in dev, so
 * the socket goes to the current origin and through Vite's proxy; in production
 * the app and the API are the same origin. `client.ts` states the rule this
 * follows — *"sending a credential to whatever host an env var happened to name
 * is how a credential leaves the building"*.
 */
function baseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? ''
}

/**
 * Open the message channel, and return the function that closes it.
 *
 * 🔴 ONE CHANNEL FOR BOTH SIDES. A teacher and a parent join the same namespace
 * and each lands in a room of one, keyed on the id in their own token — nobody
 * asks for a room. That is why the same function serves both tiers rather than
 * there being a teacher's socket and a parent's.
 *
 * ⚠️ BOUNDED RECONNECTION. A parent's access token is fifteen minutes with no
 * refresh route (OD-51), so once it expires every reconnect is refused — and
 * socket.io's default is to try forever, which on a phone left open overnight is
 * a battery drain against a door that will not open until they sign in again.
 */
export function openMessages(token: string, onMessage: (event: MessagePush) => void): () => void {
  const socket: Socket = io(`${baseUrl()}/messages`, {
    auth: { token },
    transports: ['websocket', 'polling'],
    withCredentials: true,
    reconnectionAttempts: 10,
    reconnectionDelayMax: 20_000,
  })

  socket.on('message.event', (event: MessagePush) => {
    onMessage(event)
  })

  return () => {
    /* ⚠️ `off` BEFORE `disconnect`: a frame delivered during teardown would
       otherwise call into a component that has already unmounted. */
    socket.off('message.event')
    socket.disconnect()
  }
}
